import { useEffect, useMemo, useReducer } from 'react'
import { TextField, Typography, Button } from '@mui/material'
import UserForm from '../../Admin/Users/UserAdmin/UserForm'

import {
    SaveButton,
    Grid,
    FormSection,
    Autocomplete,
    UniversalAccordion,
    UniversalModal,
} from '../../UniversalComponents'

import { useToastMessage } from '../../../context/ToastPopUpContext'
import { useAuth } from '../../../context/AuthenticationContext'
import responseError from '../../../context/responseError/responseError'
import TicketCommentCard from './TicketCommentCard'
import { editSupportTicket } from '../../../apiRoutes'

interface TicketFormData {
    id?: string
    _id?: string
    email: string
    firstName?: string
    lastName?: string
    phoneNumber?: string
    message?: string
    updatedBy?: string
    createdBy?: string
    updatedAt?: string
    createdAt?: string
    ticketType?: string
    manufacturer?: string
    user?: any
    platform?: string
    appVersion?: string
    ticketNumber?: string
    ticketStatus?: string
    ticketComment?: Object[]
    ticketMessageToCustomer?: Object[]
}
interface TicketFormTypes {
    row: TicketFormData
    formDescription?: string
    userFormPermissionCheck?: boolean
    onClose?: any
    saveCache?: any
    minimizeModal?: () => void
    userAccount?: boolean
    disableDeactivation?: boolean
}

const TicketForm = ({ row, onClose }: TicketFormTypes) => {
    const setToastMessage = useToastMessage()
    const currentUser = useAuth()
    const auth: any = currentUser.auth

    const defaultState = {
        formData: {
            id: row?._id || '',
            email: row?.email || '',
            firstName: row?.firstName || '',
            lastName: row?.lastName || '',
            phoneNumber: row?.phoneNumber || '',
            manufacturer: row.manufacturer || {},
            user: row?.user || {},
            platform: row?.platform || '',
            appVersion: row?.appVersion || '',
            ticketNumber: row?.ticketNumber || '',
            ticketStatus: row?.ticketStatus || '',
            ticketComment: row?.ticketComment || [],
            ticketMessageToCustomer: row?.ticketMessageToCustomer || [],
            message: row?.message || '',
        },
        formValidation: {
            isFormDirty: false,
            isFormValid: false,
        },
        newTicketMessageToCustomer: '',
        newTicketComment: '',
        expandMessageToCustomerHistory: false,
        expandInternalCommentHistory: false,
        userFormVisible: false,
    }

    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        formData,
        formValidation,
        newTicketMessageToCustomer,
        newTicketComment,
        expandMessageToCustomerHistory,
        expandInternalCommentHistory,
        userFormVisible,
    } = state

    const { isFormDirty, isFormValid } = formValidation

    const {
        id,
        email,
        firstName,
        lastName,
        phoneNumber,
        manufacturer,
        user,
        platform,
        appVersion,
        ticketNumber,
        ticketComment,
        ticketMessageToCustomer,
        ticketStatus,
        message,
    } = formData

    const handleSubmit = async () => {
        try {
            let parsedFormData = {
                ...formData,
                _id: id,
            }
            delete parsedFormData.id
            if (parsedFormData && !parsedFormData?.user?._id) {
                delete parsedFormData.user
            }
            let res: any
            if (row.id) {
                res = await editSupportTicket(parsedFormData)
                if (res) {
                    const { data, status } = res
                    if (data && status === 200) {
                        setToastMessage(
                            `Support Ticket: ${data.ticketNumber} was edited successfully`,
                            'success'
                        )

                        onClose(row._id, data)
                    } else {
                        responseError(res, row)
                    }
                }
            } else {
                res = await editSupportTicket(parsedFormData)
                if (res) {
                    const { data, status } = res
                    if (data?.status === 'failed') {
                        setToastMessage(`User: ${data?.message}`, 'error')
                    } else if (data && status === 200) {
                        setToastMessage(
                            `Support Ticket: ${data.ticketNumber} was edited successfully`,
                            'success'
                        )
                        onClose(row._id, data)
                    } else {
                        responseError(res, row)
                    }
                }
            }
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(
                `Support Ticket: ${row?.ticketNumber} was edited error.`,
                'error'
            )
        }
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target
        if (
            name === 'newTicketComment' ||
            name === 'newTicketMessageToCustomer'
        ) {
            setState({
                [name]: value,
            })
        } else {
            setState({
                formData: { ...formData, [name]: value },
                formValidation: { ...formValidation, isFormDirty: true },
            })
        }
    }

    const handleComment = (commentType: string) => {
        if (commentType === 'ticketMessageToCustomer') {
            const comment = {
                comment: newTicketMessageToCustomer,
                commentBy: auth?.user?.userEmail,
                commentDate: new Date().toISOString(),
            }
            setState({
                formData: {
                    ...formData,
                    ticketMessageToCustomer: [
                        ...ticketMessageToCustomer,
                        comment,
                    ],
                },
                newTicketMessageToCustomer: '',
                expandMessageToCustomerHistory: !expandMessageToCustomerHistory,
                formValidation: { ...formValidation, isFormDirty: true },
            })
        } else {
            const comment = {
                comment: newTicketComment,
                commentBy: auth?.user?.userEmail,
                commentDate: new Date().toISOString(),
            }
            setState({
                formData: {
                    ...formData,
                    ticketComment: [...ticketComment, comment],
                },
                newTicketComment: '',
                expandInternalCommentHistory: !expandInternalCommentHistory,
                formValidation: { ...formValidation, isFormDirty: true },
            })
        }
    }

    const handleDeleteComment = (commentType, commentDate) => {
        const updatedComments = state?.formData?.[commentType].filter(
            (message: any) => {
                return message.commentDate !== commentDate
            }
        )
        setState({
            formData: {
                ...formData,
                [commentType]: updatedComments,
            },
            formValidation: { ...formValidation, isFormDirty: true },
        })
    }

    const setModalVisbility = async (user?, newData?) => {
        setState({
            userFormVisible: !userFormVisible,
            formData: { ...formData, user: newData || formData.user },
        })
    }

    useEffect(() => {
        if (isFormDirty) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [isFormDirty])

    const userEditingPermission = auth?.userPermissions?.User?.write === true
    const superAdmin = auth?.userProfile.superAdmin === true

    const ticketStatusOptions = ['Open', 'In Progress', 'Closed']

    const messageToCustomer =
        ticketMessageToCustomer?.length !== row?.ticketMessageToCustomer?.length
    const internalComment = ticketComment?.length !== row?.ticketComment?.length
    const submitButtonText =
        (messageToCustomer &&
            internalComment &&
            'Notify Customer & Save Internal Comments') ||
        (messageToCustomer && 'Notify Customer') ||
        (internalComment && 'Save Changes and Internal Comments') ||
        'Save Changes'

    const deleteCommentCheck = ({ commentType, index, commentDate }: any) => {
        const originalLength = row?.[commentType]?.length || 0
        const currentLength = state.formData?.[commentType]?.length || 0

        if (originalLength !== currentLength && index > originalLength - 1) {
            return () => handleDeleteComment(commentType, commentDate)
        } else {
            return undefined
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sx={{ marginTop: 5 }}>
                <Grid container spacing={2} alignItems="center">
                    <FormSection title="Ticket Status" titleAlignment="center">
                        <Grid item xs={12}>
                            <Autocomplete
                                id="ticketStatus"
                                options={ticketStatusOptions}
                                handleChange={handleChange}
                                value={ticketStatus}
                                label="Ticket Status"
                                name="ticketStatus"
                                sx={{
                                    flexGrow: 1,
                                }}
                            />
                        </Grid>
                    </FormSection>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormSection
                            title="Ticket Information"
                            titleAlignment="center"
                        >
                            <Grid item md={7} xs={12}>
                                <TextField
                                    required={true}
                                    label="E-Mail"
                                    name="email"
                                    fullWidth
                                    value={email}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={5} xs={12}>
                                <TextField
                                    required={true}
                                    label="Manufacturer"
                                    name="manufacturer"
                                    fullWidth
                                    value={manufacturer?.manufacturerName || ''}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={firstName}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={lastName}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="phoneNumber"
                                    value={phoneNumber}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    fullWidth
                                    label="Ticket Number"
                                    name="ticketNumber"
                                    value={ticketNumber}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    fullWidth
                                    label="Platform"
                                    name="platform"
                                    value={platform}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    fullWidth
                                    label="App Version"
                                    name="appVersion"
                                    value={appVersion}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item md={12} xs={12}>
                                <TextField
                                    fullWidth
                                    label="Original Ticket Message"
                                    name="message"
                                    value={message}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    name="viewUserAccount"
                                    onClick={setModalVisbility}
                                    disabled={!user._id}
                                    sx={{
                                        width: '100%',
                                        height: 55,
                                    }}
                                >
                                    {!user?._id
                                        ? 'No User Linked to Ticket'
                                        : 'View User Account'}
                                </Button>
                            </Grid>
                        </FormSection>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <FormSection
                            title="Ticket Messages to Customer"
                            titleAlignment="center"
                        >
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="New Ticket Message To Customer"
                                    name="newTicketMessageToCustomer"
                                    onChange={handleChange}
                                    value={newTicketMessageToCustomer}
                                    onKeyDown={(e) =>
                                        e.key === 'Enter' &&
                                        handleComment('ticketMessageToCustomer')
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    name="sumbitNewTicketMessageToCustomer"
                                    onClick={() =>
                                        handleComment('ticketMessageToCustomer')
                                    }
                                    disabled={!newTicketMessageToCustomer}
                                    sx={{
                                        width: '100%',
                                        height: 55,
                                    }}
                                >
                                    Add Message to Customer
                                </Button>
                            </Grid>
                            <UniversalAccordion
                                accordionTitle="Messages to Customer History"
                                accordionStyle={{
                                    backgroundColor: 'rgb(53, 53, 53)',
                                }}
                                expanded={expandMessageToCustomerHistory}
                                onClick={() =>
                                    setState({
                                        expandMessageToCustomerHistory:
                                            !expandMessageToCustomerHistory,
                                    })
                                }
                            >
                                <Grid item md={12} xs={12}>
                                    {ticketMessageToCustomer?.length !== 0 ? (
                                        ticketMessageToCustomer.map(
                                            (comment: any, index: number) => (
                                                <TicketCommentCard
                                                    data={comment}
                                                    key={`ticket-message-to-customer-${index}`}
                                                    onDelete={deleteCommentCheck(
                                                        {
                                                            commentType:
                                                                'ticketMessageToCustomer',
                                                            index,
                                                            commentDate:
                                                                comment.commentDate,
                                                        }
                                                    )}
                                                />
                                            )
                                        )
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No messages to customer.
                                        </Typography>
                                    )}
                                </Grid>
                            </UniversalAccordion>
                        </FormSection>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <FormSection
                            title="Internal Ticket Comments"
                            titleAlignment="center"
                        >
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="New Internal Comment"
                                    name="newTicketComment"
                                    onChange={handleChange}
                                    value={newTicketComment}
                                    onKeyDown={(e) =>
                                        e.key === 'Enter' &&
                                        handleComment('ticketComment')
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    name="sumbitNewTicketComment"
                                    onClick={() =>
                                        handleComment('ticketComment')
                                    }
                                    disabled={!newTicketComment}
                                    sx={{
                                        width: '100%',
                                        height: 55,
                                    }}
                                >
                                    Add New Internal Message
                                </Button>
                            </Grid>
                            <UniversalAccordion
                                accordionTitle="Internal Comment History"
                                accordionStyle={{
                                    backgroundColor: 'rgb(53, 53, 53)',
                                }}
                                expanded={expandInternalCommentHistory}
                                onClick={() =>
                                    setState({
                                        expandInternalCommentHistory:
                                            !expandInternalCommentHistory,
                                    })
                                }
                            >
                                <Grid item md={12} xs={12}>
                                    {ticketComment?.length !== 0 ? (
                                        ticketComment.map(
                                            (comment: any, index: number) => (
                                                <TicketCommentCard
                                                    data={comment}
                                                    key={`ticket-internal-comment-${index}`}
                                                    onDelete={deleteCommentCheck(
                                                        {
                                                            commentType:
                                                                'ticketComment',
                                                            index,
                                                            commentDate:
                                                                comment.commentDate,
                                                        }
                                                    )}
                                                />
                                            )
                                        )
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No Internal Ticket Comments
                                        </Typography>
                                    )}
                                </Grid>
                            </UniversalAccordion>
                        </FormSection>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <SaveButton
                    handleSubmit={handleSubmit}
                    handleReset={() => setState(defaultState)}
                    disabledSave={!isFormValid}
                    disabledReset={!isFormDirty}
                    saveTitle={submitButtonText}
                />
            </Grid>
            <UniversalModal
                onClose={setModalVisbility}
                open={userFormVisible}
                title={` User ID: ${user?._id}`}
                timeStamp={` Username: ${user.userEmail}`}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                <UserForm
                    row={user}
                    userAccount={true}
                    onClose={(user: any, newData?: any) =>
                        setModalVisbility(user, newData)
                    }
                    userFormPermissionCheck={false}
                />
            </UniversalModal>
        </Grid>
    )
}

export default TicketForm
